import { ContainerState } from 'containers/CheckoutPage/types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';

import { Button, Modal } from 'antd';
import ChargeBasedOnActualWeightIconModal from 'components/ChargeBasedOnActualWeightIconModal';
import ServiceFeeNote from 'components/ServiceFeeNote';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import VatTooltip from 'components/VatTooltip';
import { Viewport } from 'components/ViewportProvider';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import translations from 'translations';
import Money from 'components/Money/Money';
import CartTotalProgressSection from '../CartTotalProgressSection';

import ArrowRightIcon from '../icons/arrowRightIcon.svg';
import CheckIcon from '../icons/checkIcon.svg';
import DangerIcon from '../icons/danger.svg';

const CardWrapper = styled.div`
  margin: 0px 8px 8px;
  padding: 16px;
  background: #fff;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
  border-radius: 8px;

  ${media.lg`
    margin: 0;
    padding: 20px;
    gap: 16px;
    box-shadow: 0 4px 24px 0 rgba(58, 85, 9, 0.04);
  `}
`;

const Title = styled.div`
  font-size: 20px;
  line-height: 28px;
  color: #111827;
  font-weight: 600;
`;

const BillDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
`;

const BillDetailColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const BillDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BillDetailLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BillDetailLabel = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
  font-weight: 500;

  ${media.lg`
    font-size: 14px;
    line-height: 22px;
  `}

  padding-bottom: ${({ paddingBottom }) => (paddingBottom ? paddingBottom : '0')};
`;

const BillDetailValue = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: ${({ isDiscount }) => (isDiscount ? '#43A047' : '#111827')};
  font-weight: 500;

  ${media.lg`
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
  `}
`;

const TotalOrderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalOrderLabel = styled.span`
  font-size: 16px;
  line-height: 24px;
  color: #303643;
  font-weight: 500;
`;

const TotalOrderValueContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
`;

const TotalOrderFinalValue = styled.span`
  font-size: 20px;
  line-height: 28px;
  color: #303643;
  font-weight: 600;
`;

const TotalOrderOriginalValue = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: #757d73;
  font-weight: 400;
  text-decoration: line-through;
  margin-bottom: 3px;
`;

const TotalByWeightTooltipContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
`;

const TotalByWeightText = styled.div`
  font-size: 10px;
  line-height: 14px;
  color: #4b5563;
  font-weight: 400;
  font-style: italic;

  ${media.lg`
    font-size: 12px;
    line-height: 16px;
    color: #4b5563;
  `}
`;

const NoteIconWrapper = styled.div`
  margin-top: 2px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #4b5563;
`;

const SavedValueText = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #e56910;
  font-weight: 500;
  text-align: right;
`;

const NextButton = styled(Button)`
  width: 100%;
  height: 44px;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  border: none !important;
  background: #43a047 !important;
  color: #ffffff !important;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 4px;

  &:disabled {
    background: #f9fafb !important;
    color: #bfc3cb !important;
  }
`;

const ConfirmSameDayDeliveryModal = styled(Modal)`
  .ant-modal-content {
    padding: 24px 16px 12px;
  }
  .ant-modal-title {
    font-size: 24px;
    font-weight: 500;
    color: #595959;
    margin-bottom: 20px !important;
  }
  .ant-modal-footer {
    display: flex;
    justify-content: center;
    button {
      flex-grow: 1;
    }
    margin-top: 20px !important;
  }
`;

const ConfirmModalTitle = styled.div`
  margin: -8px -16px 0;
  padding: 0 16px 16px;
  border-bottom: 1px solid #e0e0e0;
`;
const ConfirmButton = styled(Button)`
  border-radius: 4px !important;
`;
const ConfirmContentModal = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #eaebea;
`;

const FooterInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FooterInfoItemTitle = styled.div`
  font-size: 14px;
  line-height: 18px;
  color: #4b5563;
  font-weight: 400;
`;

interface CartTotalBillProps {
  currentStep: number;
  deliveryFee: any;
  subTotal: number;
  subTotalBeforeVat: number;
  cart: any;
  orderDisabled: boolean;
  loading?: boolean;
  onGoNext: () => void;
  voucherState: ContainerState['vouchers'];
  netTotalOrder: number;
  grossTotalOrder: number;
  totalDiscount?: number;
  deliveryDate?: string;
  setOpenCalendar?: (open: boolean) => void;

  openConfirmSameDayDeliveryModal?: boolean;
  setOpenConfirmSameDayDeliveryModal?: (open: boolean) => void;

  parentRef?: React.RefObject<HTMLDivElement>;
  boundaryRef?: React.RefObject<HTMLDivElement>;
  voucherRef?: React.RefObject<HTMLDivElement>;

  // hide saving amount section
  isHideSavingAmount?: boolean;
}

const CartTotalBill = (props: CartTotalBillProps) => {
  const {
    currentStep,
    subTotal,
    subTotalBeforeVat,
    deliveryFee,
    cart,
    voucherState: { itemDiscount, discountedValue: totalDiscountVoucher },
    netTotalOrder,
    grossTotalOrder,
    totalDiscount,
    loading,
    orderDisabled,
    deliveryDate,

    onGoNext,
    setOpenCalendar,
    openConfirmSameDayDeliveryModal,
    setOpenConfirmSameDayDeliveryModal,
    parentRef,
    boundaryRef,
    voucherRef,
    isHideSavingAmount = false,
  } = props;

  const loggedIn = !!localStorage.getItem('token');
  const rightSectionRef = useRef<HTMLDivElement>(null);

  const lastStickyState = useRef({ isSticky: false, top: 115 });
  const [, forceUpdate] = useState(0);

  // calculate data
  const totalVatOfDiscountVoucher = useMemo(() => {
    return (
      itemDiscount?.reduce((acc, item) => {
        const vat = cart?.cartSet?.[0]?.items?.find((it) => it?.product?.id === item?.id)?.product?.priceV2?.vat || 0;
        return acc + Math.round(((item?.discountValue || 0) * vat) / 100);
      }, 0) || 0
    );
  }, [cart?.cartSet, itemDiscount]);

  const totalVat = useMemo(() => {
    const serviceFeeVat = cart?.deliveryFee?.isFreeDelivery ? 0 : cart?.deliveryFee?.additionalVat;
    const totalVatOnProducts = cart?.cartSet?.[0]?.totalVatOnProducts || 0;

    return serviceFeeVat + totalVatOnProducts - totalVatOfDiscountVoucher;
  }, [cart?.deliveryFee, cart?.cartSet, totalVatOfDiscountVoucher]);

  // sticky
  const STICKY_TOP_OFFSET = 115;

  const calculateSticky = useCallback(() => {
    const el = rightSectionRef.current;
    if (!el || !parentRef?.current || !boundaryRef?.current || window.document.body.clientWidth < 1024) {
      return;
    }

    const cardHeight = el.offsetHeight;
    const parentRect = parentRef.current.getBoundingClientRect();
    const boundaryRect = boundaryRef.current.getBoundingClientRect();

    const startPoint = voucherRef?.current ? voucherRef.current.getBoundingClientRect().bottom : parentRect.top;
    const shouldStartFix = startPoint <= STICKY_TOP_OFFSET;

    if (!shouldStartFix) {
      if (lastStickyState.current.isSticky) {
        lastStickyState.current = { isSticky: false, top: STICKY_TOP_OFFSET };
        el.style.position = '';
        el.style.top = '';
        el.style.width = '';
        forceUpdate((n) => n + 1);
      }
      return;
    }

    const maxTop = Math.floor(boundaryRect.bottom - cardHeight);
    const nextTop = Math.min(STICKY_TOP_OFFSET, maxTop);

    const parentWidth = parentRef.current.offsetWidth;
    el.style.position = 'fixed';
    el.style.top = `${nextTop}px`;
    el.style.width = parentWidth ? `${parentWidth}px` : '';

    if (!lastStickyState.current.isSticky) {
      lastStickyState.current.isSticky = true;
      forceUpdate((n) => n + 1);
    }
    lastStickyState.current.top = nextTop;
  }, [parentRef, boundaryRef, voucherRef]);

  // handlers
  const onCreateOrder = useCallback(() => {
    if (openConfirmSameDayDeliveryModal) {
      setOpenConfirmSameDayDeliveryModal?.(false);
    }
    onGoNext();
  }, [openConfirmSameDayDeliveryModal, onGoNext]);

  const proceedToCreateOrderDraft = useCallback(() => {
    if (moment(deliveryDate, 'DD/MM/YYYY').isSame(moment(), 'day')) {
      setOpenConfirmSameDayDeliveryModal?.(true);
      return;
    }

    onCreateOrder();
  }, [deliveryDate, onCreateOrder, setOpenConfirmSameDayDeliveryModal]);

  // effects
  useEffect(() => {
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        calculateSticky();
        rafId = null;
      });
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [calculateSticky]);

  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isDesktop = width >= 1024;
        return (
          <CardWrapper ref={rightSectionRef}>
            <Title>
              <FormattedMessage {...utilsMessages.orderSummary} />
            </Title>
            <BillDetailContainer>
              <BillDetailColumn>
                <BillDetailRow>
                  <BillDetailLabel>
                    <FormattedMessage {...utilsMessages.subtotalExclVatSummary} />
                  </BillDetailLabel>
                  <BillDetailValue>
                    <Money>{subTotalBeforeVat}</Money>
                  </BillDetailValue>
                </BillDetailRow>
                <TotalByWeightTooltipContainer>
                  <TotalByWeightText>
                    <FormattedMessage {...utilsMessages.paymentByUomKg} />
                  </TotalByWeightText>
                  <NoteIconWrapper>
                    <ChargeBasedOnActualWeightIconModal width={11} height={11} />
                  </NoteIconWrapper>
                </TotalByWeightTooltipContainer>
              </BillDetailColumn>
              {loggedIn && (
                <BillDetailRow>
                  <BillDetailLabelContainer>
                    <BillDetailLabel>
                      <FormattedMessage {...utilsMessages.serviceFeeExclVat} />
                    </BillDetailLabel>
                    <ServiceFeeNote
                      deliveryFeeThreshold={deliveryFee?.deliveryFeeThreshold}
                      moq={cart?.store?.moq}
                      deliveryFee={deliveryFee?.fee}
                      areaGroupName={deliveryFee?.areaGroupName}
                    />
                  </BillDetailLabelContainer>
                  <BillDetailValue isDiscount={deliveryFee?.isFreeDelivery}>
                    {deliveryFee?.isFreeDelivery ? (
                      <FormattedMessage {...utilsMessages.free} />
                    ) : (
                      <Money>{deliveryFee?.fee}</Money>
                    )}
                  </BillDetailValue>
                </BillDetailRow>
              )}
              <BillDetailRow>
                <BillDetailLabelContainer>
                  <BillDetailLabel paddingBottom={'2px'}>
                    <FormattedMessage {...utilsMessages.totalVatSummary} />
                  </BillDetailLabel>
                  <VatTooltip
                    productVat={cart?.cartSet?.[0]?.totalVatOnProducts}
                    serviceFeeVat={cart?.deliveryFee?.isFreeDelivery ? 0 : cart?.deliveryFee?.additionalVat}
                    voucherVat={totalVatOfDiscountVoucher}
                  />
                </BillDetailLabelContainer>
                <BillDetailValue>
                  <Money>{totalVat}</Money>
                </BillDetailValue>
              </BillDetailRow>
              {loggedIn && totalDiscountVoucher > 0 && (
                <BillDetailRow>
                  <BillDetailLabel>
                    <FormattedMessage {...utilsMessages.promotionDiscount} />
                  </BillDetailLabel>
                  <BillDetailValue isDiscount>
                    -<Money>{totalDiscountVoucher}</Money>
                  </BillDetailValue>
                </BillDetailRow>
              )}
            </BillDetailContainer>
            {isDesktop && (
              <>
                {loggedIn && currentStep === 1 && (
                  <CartTotalProgressSection deliveryFee={deliveryFee} subTotal={subTotal} cartMoq={cart?.store?.moq} />
                )}
                <Divider />
                <div>
                  <TotalOrderContainer>
                    <TotalOrderLabel>
                      <FormattedMessage {...utilsMessages.totalPayable} />
                    </TotalOrderLabel>
                    <TotalOrderValueContainer>
                      {grossTotalOrder !== netTotalOrder && (
                        <TotalOrderOriginalValue>
                          <Money>{grossTotalOrder}</Money>
                        </TotalOrderOriginalValue>
                      )}
                      <TotalOrderFinalValue>
                        <Money>{netTotalOrder}</Money>
                      </TotalOrderFinalValue>
                    </TotalOrderValueContainer>
                  </TotalOrderContainer>
                  {!isHideSavingAmount && !!totalDiscount && (
                    <SavedValueText>
                      <FormattedMessage {...utilsMessages.saveAmount} />
                      <span>
                        {' '}
                        <Money>{totalDiscount}</Money>
                      </span>
                    </SavedValueText>
                  )}
                </div>
                <NextButton
                  disabled={orderDisabled}
                  loading={loading}
                  onClick={() => {
                    if (currentStep === 3) {
                      proceedToCreateOrderDraft();
                    } else {
                      onGoNext();
                    }
                  }}
                  type="primary"
                >
                  {currentStep === 1 ? (
                    <FormattedMessage {...utilsMessages.placeYourOrder} />
                  ) : (
                    <FormattedMessage {...utilsMessages.makeOrder} />
                  )}
                  {currentStep === 1 && <SVGIcon src={ArrowRightIcon} renderAsImage={false} />}
                </NextButton>
              </>
            )}
            <Divider />
            <FooterInfoItem>
              <SVGIcon src={CheckIcon} renderAsImage={false} />
              <FooterInfoItemTitle>
                <FormattedMessage {...utilsMessages.paymentUponDelivery} />
              </FooterInfoItemTitle>
            </FooterInfoItem>
            <FooterInfoItem>
              <SVGIcon src={CheckIcon} renderAsImage={false} />
              <FooterInfoItemTitle>
                <FormattedMessage {...utilsMessages.returnPolicy} />
              </FooterInfoItemTitle>
            </FooterInfoItem>
            <FooterInfoItem>
              <SVGIcon src={CheckIcon} renderAsImage={false} />
              <FooterInfoItemTitle>
                <FormattedMessage {...utilsMessages.vatOnDelivery} />
              </FooterInfoItemTitle>
            </FooterInfoItem>
            <ConfirmSameDayDeliveryModal
              open={openConfirmSameDayDeliveryModal}
              title={
                <ConfirmModalTitle>
                  <FormattedMessage {...utilsMessages.notice} />
                </ConfirmModalTitle>
              }
              centered
              onOk={() => {
                setOpenCalendar?.(true);
                setOpenConfirmSameDayDeliveryModal?.(false);
              }}
              onCancel={() => setOpenConfirmSameDayDeliveryModal?.(false)}
              closable={false}
              okText={translations(utilsMessages.changeDay)}
              okButtonProps={{
                size: 'large',
                style: {
                  borderRadius: '4px',
                },
              }}
              cancelButtonProps={{
                size: 'large',
                style: {
                  borderRadius: '4px',
                },
              }}
              footer={(_, { OkBtn }) => (
                <>
                  <ConfirmButton size="large" onClick={onCreateOrder}>
                    <FormattedMessage {...utilsMessages.confirm} />
                  </ConfirmButton>
                  <OkBtn />
                </>
              )}
            >
              <ConfirmContentModal>
                <SVGIcon src={DangerIcon} />
                <FormattedMessage {...utilsMessages.confirmSamDayDelivery} />
              </ConfirmContentModal>
            </ConfirmSameDayDeliveryModal>
          </CardWrapper>
        );
      }}
    </Viewport.Consumer>
  );
};

export default CartTotalBill;
